import { apiTypes, CenteredPage, urlUtil } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import routeFactory from '../../common/util/routeFactory';
import type { CreateUserSignupDetailsDto } from './ducks';
import {
  selectEmailParts,
  selectInvestorSignupParts,
  selectTokenParts,
  submitInvestorSignupForm,
  validateInvestorSignupEmail,
  validateInvestorSignupToken,
} from './ducks';
import InvestorSignupForm from './form/InvestorSignupForm';
import {
  CMGBarImage,
  SDiv,
  SFooter,
  SLeft,
  SLinkButton,
  SRight,
  SSectionSubtitle,
  SSectionSubtitleDescription,
  SSectionTitle,
  SSectionTitleDescription,
} from './InvestorSignupRoute.styles';

export const InvestorSignupRouteComponent: React.FC = () => {
  const [investorSignupEmail, setInvestorSignupEmail] = React.useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { status: investorSignupStatus } = useSelector(selectInvestorSignupParts);

  const { data: tokenData } = useSelector(selectTokenParts);

  const { isMatchFound: isEmailValid } = useSelector(selectEmailParts).data ?? {
    isMatchFound: false,
  };

  const { token } = urlUtil.queryParse<{ token: string }>(location.search);

  useEffect(() => {
    dispatch(
      validateInvestorSignupToken({
        token: token,
      })
    );

    /**
     * Redirect to error page if token is invalid
     */
    if (tokenData?.isValid === false) {
      navigate(
        routeFactory.error.getUrlPath({ errorCode: apiTypes.ServiceErrorCode.INVALID_TOKEN })
      );
    }
  }, [dispatch, navigate, token, tokenData?.isValid]);

  const validateEmail = useCallback(() => {
    if (investorSignupEmail) {
      dispatch(
        validateInvestorSignupEmail({
          token: token,
          emailAddress: investorSignupEmail,
        })
      );
    }
  }, [dispatch, investorSignupEmail, token]);

  const handleSubmit = useCallback(
    (values: CreateUserSignupDetailsDto) => {
      dispatch(submitInvestorSignupForm({ values: values }));
    },
    [dispatch]
  );

  if (investorSignupStatus === 'SUCCEEDED') {
    SnackbarManager.success('Successfully submitted form');
    return <Navigate to={routeFactory.investorSignupSuccess.getUrlPath()} replace />;
  } else if (investorSignupStatus === 'FAILED') {
    return (
      <Navigate
        to={routeFactory.error.getUrlPath({
          errorCode: apiTypes.ServiceErrorCode.VALIDATION_ERROR,
        })}
        replace
      />
    );
  }

  return (
    <SDiv>
      <SLeft>
        <CenteredPage title="Register" hideFooter hideBackground>
          <InvestorSignupForm
            isEmailValid={isEmailValid}
            validateEmail={validateEmail}
            onEmailChange={setInvestorSignupEmail}
            handleSubmit={handleSubmit}
            token={token}
          />
          <SFooter>
            Contact CMG <SLinkButton href="mailto:support@cmgx.io">here</SLinkButton>
          </SFooter>
        </CenteredPage>
      </SLeft>
      <SRight>
        <SSectionTitle>CMG, the digital network for capital</SSectionTitle>
        <SSectionTitleDescription>
          CMG is modernizing the capital formation process by connecting buy- and sell-side market
          participants
        </SSectionTitleDescription>
        <CMGBarImage />
        <SSectionSubtitle>Market Intelligence</SSectionSubtitle>
        <SSectionSubtitleDescription>
          Monitor real-time ECM deal launches supporting IPO, Secondary, and Block offerings via the
          XC calendar, powered by the premier source of structured deal data.
        </SSectionSubtitleDescription>
        <SSectionSubtitle>Enhanced Collaboration</SSectionSubtitle>
        <SSectionSubtitleDescription>
          Standardized ECM workflows allow institutional investors to optimize the investment
          decision process while increasing transparency and minimizing risk.
        </SSectionSubtitleDescription>
        <SSectionSubtitle>Streamlined Communication</SSectionSubtitle>
        <SSectionSubtitleDescription>
          CMG improves a fragmented and inefficient process by enabling investors and underwriters
          to communicate directly on offering terms, meeting requests, indications and allocations
          via a purpose-built ECM platform.
        </SSectionSubtitleDescription>
      </SRight>
    </SDiv>
  );
};

export default InvestorSignupRouteComponent;
